import { createContext, memo, ReactNode, useContext, useEffect, useMemo, useReducer } from "react";
import { AresKeycloak, keycloakInstance } from "./instance";
import { TokenData } from "./types";

export type KeycloakContextType = {
  keycloak: AresKeycloak,
  roles: string[],
  tokenData?: TokenData
}

const keycloakContext = createContext<KeycloakContextType>({
  keycloak: keycloakInstance,
  roles: [],
});

export const KeycloakProvider = memo<{ children: ReactNode }>(({ children }) => {
  const [ _, forceUpdate ] = useReducer(prevState => prevState + 1, 0);

  useEffect(() => {
    keycloakInstance.addListener("onAuthSuccess", forceUpdate);
    keycloakInstance.addListener("onAuthRefreshSuccess", forceUpdate);
    return () => {
      keycloakInstance.removeListener("onAuthSuccess", forceUpdate);
      keycloakInstance.removeListener("onAuthRefreshSuccess", forceUpdate);
    };
  }, []);

  useEffect(() => {
    keycloakInstance.initWithDefaults()
      .then(() => {
        if (!keycloakInstance.authenticated) {
          keycloakInstance.login();
        }
      })
      .catch(console.error);
  }, []);

  const contextValue: KeycloakContextType = useMemo(() => {
    return ({
      keycloak: keycloakInstance,
      roles: keycloakInstance.realmAccess?.roles || [],
      tokenData: keycloakInstance.getTokenData(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ _ ]);

  return (
    <keycloakContext.Provider value={contextValue}>
      {keycloakInstance.authenticated ? children : null}
    </keycloakContext.Provider>
  );
});

export const useKeycloak = () => useContext(keycloakContext);